// Progressive resizing for the app's ordinary data tables. Tables with their own
// React resize handles retain that implementation; no React-managed children are
// inserted into headers, so sorting and table reconciliation stay untouched.
export const AUTO_COL_MIN = 56;
export const AUTO_COL_MAX = 560;

export function clampColumnWidth(width, min = AUTO_COL_MIN, max = AUTO_COL_MAX) {
  return Math.max(min, Math.min(max, Math.round(width)));
}

// Rond de gemeten kolomgrenzen af, niet elke breedte afzonderlijk. Zo blijft
// de totale beginbreedte binnen de gemeten tabel, ook bij fractionele pixels.
// Alleen de eerste meting: opgeslagen en door de gebruiker gekozen maten winnen.
export function roundMeasuredTableWidths(widths) {
  let measuredEdge = 0;
  let appliedEdge = 0;
  return widths.map(width => {
    measuredEdge += Math.max(24, width);
    const nextEdge = Math.floor(measuredEdge + 1e-7);
    const rounded = nextEdge - appliedEdge;
    appliedEdge = nextEdge;
    return rounded;
  });
}

// Een balansraster (data-resize-grid-balance) vult zijn container: de opgeslagen
// of gemeten breedtes zijn een VOORKEUR, wat op het scherm komt is die voorkeur
// passend gemaakt op de ruimte die er nu is. Zonder deze stap bleven de pixels
// van een bredere kaart of een ouder scherm staan en schoof de laatste kolom
// (DMU en het actiemenu van een contactrij) achter een horizontale schuifbalk.
// Krimpen en groeien gaan naar rato van de speling boven de kolombodem, dus een
// vaste kolom (breedte gelijk aan zijn bodem) blijft wat hij is. Past het ook op
// de bodems niet, dan komen de bodems terug en scrolt de rij, zoals bedoeld.
export function fitGridWidths(widths, mins, available) {
  if (!Number.isFinite(available) || available <= 0) return widths.slice();
  const floor = widths.map((width, index) => Math.min(width, Number.isFinite(mins?.[index]) && mins[index] > 0 ? mins[index] : AUTO_COL_MIN));
  const slack = widths.map((width, index) => width - floor[index]);
  const totalSlack = slack.reduce((sum, value) => sum + value, 0);
  const delta = available - widths.reduce((sum, width) => sum + width, 0);
  if (delta === 0 || totalSlack <= 0) return widths.slice();
  if (-delta >= totalSlack) return floor;
  return widths.map((width, index) => Math.floor(width + delta * slack[index] / totalSlack));
}

// Een pastabel (data-resize-fit, RecordGridTable) scrolt nooit horizontaal: zijn
// kaart knipt wat uitsteekt. De kolombreedtes zijn daarom een voorkeur die
// fitGridWidths passend maakt op de ouder, bij elke maatwissel opnieuw. Het
// attribuut draagt per kolom de bodem: de vaste ontwerpbreedte, of 0 voor een
// vrije kolom (dan AUTO_COL_MIN). Zonder deze stap bleef de eerst gemeten som als
// minWidth op de tabel staan en schoof een smallere kaart (of een bredere kolom)
// de actiekolom uit beeld (gevonden 6 okt 2026).
export function parseFitMins(attr, count) {
  const parts = String(attr ?? '').split(',').map(Number);
  return Array.from({ length: count }, (_, index) => (Number.isFinite(parts[index]) && parts[index] > 0 ? parts[index] : AUTO_COL_MIN));
}

// Slepen in een pastabel ruilt breedte met de rechterbuur (de laatste kolom met
// de linker), zodat de som gelijk blijft aan wat er past. Geen van beide zakt
// onder zijn bodem; een kolom groeit dus alleen zolang de buur speling heeft.
// Geen AUTO_COL_MAX: de vrije kolom van een brede kaart is vaak breder, en de som
// begrenst het al.
export function fitNeighbourResize(widths, mins, index, wanted, max = Infinity) {
  const next = widths.slice();
  if (next.length < 2) return next;
  const neighbour = index < next.length - 1 ? index + 1 : index - 1;
  const pair = next[index] + next[neighbour];
  const own = Math.max(mins[index], Math.min(max, pair - mins[neighbour], Math.round(wanted)));
  next[index] = own;
  next[neighbour] = pair - own;
  return next;
}

export function tableColumnKey(path, table, headers) {
  const owner = table.id || table.closest('[id]')?.id || '';
  const names = headers.map((cell) => (cell.getAttribute('aria-label') || cell.textContent || '').trim().replace(/\s+/g, ' ').slice(0, 48));
  return `pb-auto-colw:${path}:${owner}:${names.join('|')}`;
}

function eligible(table) {
  if (!table.tHead?.rows?.length || table.querySelector('[role="separator"]')) return null;
  // These already manage their widths in React, including on narrow screens.
  if (table.matches('.pb-cs') || table.closest('[data-no-auto-column-resize]')) return null;
  const cells = Array.from(table.tHead.rows[0].cells);
  if (cells.length < 2 || cells.some((cell) => cell.tagName !== 'TH' || cell.colSpan !== 1 || cell.rowSpan !== 1)) return null;
  if (!table.getBoundingClientRect().width || cells.every((cell) => !cell.textContent?.trim())) return null;
  return cells;
}

export function installTableColumnResizing(doc = document) {
  const win = doc.defaultView;
  const states = new WeakMap();
  const gridStates = new WeakMap();
  const flexStates = new WeakMap();
  let queued = false;
  let active = null;
  let swallowClickUntil = 0;
  let fitObserver = null;

  const applyFit = (state) => {
    const { table, cells, cols, mins } = state;
    state.widths = state.widths.map((width, index) => Math.max(width, mins[index]));
    state.applied = fitGridWidths(state.widths, mins, table.parentElement?.clientWidth || 0);
    table.style.tableLayout = 'fixed';
    cells.forEach((cell, index) => {
      if (cols[index]) cols[index].style.width = `${state.applied[index]}px`;
      else cell.style.width = `${state.applied[index]}px`;
    });
  };
  const apply = (state) => {
    if (state.fit) { applyFit(state); return; }
    const { table, cells, widths, cols, originalMin } = state;
    const total = widths.reduce((sum, width) => sum + width, 0);
    table.style.tableLayout = 'fixed';
    table.style.minWidth = `${Math.max(originalMin, total)}px`;
    cells.forEach((cell, index) => {
      if (cols[index]) cols[index].style.width = `${widths[index]}px`;
      else cell.style.width = `${widths[index]}px`;
    });
  };

  const scan = () => {
    queued = false;
    doc.querySelectorAll('table').forEach((table) => {
      const cells = eligible(table);
      if (!cells) return;
      const previous = states.get(table);
      if (previous && previous.cells.every((cell, index) => cell === cells[index]) && previous.cells.length === cells.length) {
        apply(previous);
        return;
      }
      // Een nieuwe kopset op een tabel die al eerder gemeten is (ProcessenGrid
      // smal/breed): eerst de eigen minWidth terug, anders meten de nieuwe koppen
      // zich op de oude som en blijft die som de bodem.
      const inlineMin = previous ? previous.inlineMin : table.style.minWidth;
      if (previous) table.style.minWidth = inlineMin;
      const key = tableColumnKey(win.location.pathname, table, cells);
      const measured = roundMeasuredTableWidths(cells.map((cell) => cell.getBoundingClientRect().width));
      let saved = null;
      try { saved = JSON.parse(win.localStorage.getItem(key)); } catch { /* storage unavailable */ }
      const widths = measured.map((width, index) => Number.isFinite(saved?.[index]) ? clampColumnWidth(saved[index]) : width);
      const cols = Array.from(table.querySelectorAll(':scope > colgroup > col'));
      const originalMin = parseFloat(win.getComputedStyle(table).minWidth) || 0;
      const fit = table.hasAttribute('data-resize-fit');
      const mins = fit ? parseFitMins(table.dataset.resizeFit, cells.length) : null;
      const state = { table, cells, cols, widths, key, originalMin, inlineMin, fit, mins };
      if (fit && table.parentElement) {
        if (!fitObserver && win.ResizeObserver) fitObserver = new win.ResizeObserver(schedule);
        fitObserver?.observe(table.parentElement);
      }
      states.set(table, state);
      table.classList.add('pb-auto-resize');
      cells.forEach((cell, index) => {
        if (!cell.textContent?.trim() && !cell.getAttribute('aria-label')) return;
        cell.classList.add('pb-auto-resize-head');
        cell.dataset.autoColIndex = String(index);
        cell.tabIndex = cell.tabIndex < 0 ? 0 : cell.tabIndex;
        cell.setAttribute('aria-keyshortcuts', 'Alt+ArrowLeft Alt+ArrowRight');
        cell.title ||= 'Sleep de rechterrand om de kolombreedte aan te passen; Alt+←/→ voor 16 px';
      });
      apply(state);
    });
    doc.querySelectorAll('[data-resize-grid-header]').forEach((header) => {
      const previous = gridStates.get(header);
      if (previous && previous.group === header.dataset.resizeGridHeader && previous.cells.every((cell, index) => cell === header.children[index]) && previous.cells.length === header.children.length) return;
      const root = header.closest('[data-resize-grid-root]');
      if (!root || !header.getBoundingClientRect().width) return;
      const group = header.dataset.resizeGridHeader;
      const cells = Array.from(header.children);
      const tracks = win.getComputedStyle(header).gridTemplateColumns.split(' ').map((value) => parseFloat(value));
      const indexes = cells.map((cell, index) => Number(cell.dataset.resizeGridCol ?? index));
      if (cells.length < 2 || tracks.length < cells.length || tracks.some((value) => !Number.isFinite(value)) || indexes.some((index) => !Number.isInteger(index) || index < 0 || index >= tracks.length)) return;
      const key = `pb-auto-gridw:${win.location.pathname}:${root.dataset.resizeGridRoot || ''}:${group}`;
      let saved = null;
      try { saved = JSON.parse(win.localStorage.getItem(key)); } catch { /* storage unavailable */ }
      const widths = tracks.map((width, index) => Number.isFinite(saved?.[index]) ? clampColumnWidth(saved[index]) : Math.round(width));
      if (saved) {
        const measured = tracks.reduce((s, w) => s + w, 0);
        const current = widths.reduce((s, w) => s + w, 0);
        if (current > measured + 1 && measured > 0) {
          const scale = measured / current;
          const mins = (root.dataset.resizeGridMins || '').split(',').map(Number);
          for (let i = 0; i < widths.length; i++) {
            const mn = Number.isFinite(mins[i]) && mins[i] > 0 ? mins[i] : AUTO_COL_MIN;
            widths[i] = Math.max(mn, Math.round(widths[i] * scale));
          }
        }
      }
      const state = { header, root, group, cells, widths, key, indexes, balance: root.hasAttribute('data-resize-grid-balance') };
      gridStates.set(header, state);
      cells.forEach((cell, index) => {
        cell.classList.add('pb-auto-resize-head');
        cell.dataset.autoGridColIndex = String(indexes[index]);
        cell.tabIndex = cell.tabIndex < 0 ? 0 : cell.tabIndex;
        cell.setAttribute('aria-keyshortcuts', 'Alt+ArrowLeft Alt+ArrowRight');
        cell.title ||= 'Sleep de rechterrand om de kolombreedte aan te passen; Alt+←/→ voor 16 px';
      });
      applyGrid(state);
    });
    // React may replace rows while keeping the header mounted. Bring new rows
    // onto the saved template without rewriting React-managed children.
    doc.querySelectorAll('[data-resize-grid-header]').forEach((header) => {
      const state = gridStates.get(header);
      if (state) applyGrid(state);
    });
    doc.querySelectorAll('[data-resize-flex-root]').forEach((root) => {
      if (!root.getBoundingClientRect().width) return;
      const columns = Array.from(root.querySelectorAll(':scope > [data-resize-flex-col]'));
      if (columns.length < 2) return;
      const existing = flexStates.get(root);
      if (existing) {
        existing.columns = columns;
        columns.forEach((column) => {
          const id = column.dataset.resizeFlexCol;
          existing.widths[id] ||= Math.round(column.getBoundingClientRect().width);
          const head = column.querySelector(':scope > [data-resize-flex-head]');
          if (head && !head.classList.contains('pb-auto-resize-head')) {
            head.classList.add('pb-auto-resize-head');
            head.tabIndex = head.tabIndex < 0 ? 0 : head.tabIndex;
            head.setAttribute('aria-keyshortcuts', 'Alt+ArrowLeft Alt+ArrowRight');
            head.title ||= 'Sleep de rechterrand om de kolombreedte aan te passen; Alt+←/→ voor 16 px';
          }
        });
        applyFlex(existing);
        return;
      }
      const key = `pb-auto-flexw:${win.location.pathname}:${root.dataset.resizeFlexRoot}`;
      let saved = null;
      try { saved = JSON.parse(win.localStorage.getItem(key)); } catch { /* storage unavailable */ }
      const widths = Object.fromEntries(columns.map((column) => {
        const id = column.dataset.resizeFlexCol;
        const measured = Math.round(column.getBoundingClientRect().width);
        return [id, Number.isFinite(saved?.[id]) ? clampColumnWidth(saved[id], Number(root.dataset.resizeFlexMin) || 190, 700) : measured];
      }));
      const state = { root, columns, widths, key };
      flexStates.set(root, state);
      columns.forEach((column) => {
        const head = column.querySelector(':scope > [data-resize-flex-head]');
        if (!head) return;
        head.classList.add('pb-auto-resize-head');
        head.tabIndex = head.tabIndex < 0 ? 0 : head.tabIndex;
        head.setAttribute('aria-keyshortcuts', 'Alt+ArrowLeft Alt+ArrowRight');
        head.title ||= 'Sleep de rechterrand om de kolombreedte aan te passen; Alt+←/→ voor 16 px';
      });
      applyFlex(state);
    });
  };
  // De kop van een rasterlijst staat buiten de scrollcontainer van zijn rijen (de
  // sectiekop draagt hem), dus hij schuift niet vanzelf mee als de rijen op een
  // smalle kaart horizontaal scrollen. De kop is zelf zo breed als het raster en
  // wordt door de sectie afgekapt: scrollLeft doet daar niets, een translate wel.
  // React beheert geen transform op dit element, dus de waarde blijft staan.
  const syncHeaderScroll = (root) => {
    root.querySelectorAll('[data-resize-grid-header]').forEach((header) => {
      const row = Array.from(root.querySelectorAll('[data-resize-grid-row]')).find((item) => item.dataset.resizeGridRow === header.dataset.resizeGridHeader);
      let left = 0;
      for (let el = row?.parentElement; el && el !== root; el = el.parentElement) left += el.scrollLeft;
      const next = left ? `translateX(${-left}px)` : '';
      if (header.style.transform !== next) header.style.transform = next;
    });
  };
  const applyGrid = (state) => {
    const gap = parseFloat(win.getComputedStyle(state.header).columnGap) || 0;
    // De ruimte komt van de ouder van de eerste rij: een scrollcontainer, dus zijn
    // clientWidth groeit niet mee met de minWidth die hieronder op de rij komt.
    // Zonder rijen valt er niets te passen en blijft de voorkeur staan.
    const firstRow = state.balance ? Array.from(state.root.querySelectorAll('[data-resize-grid-row]')).find((row) => row.dataset.resizeGridRow === state.group) : null;
    const room = firstRow?.parentElement ? firstRow.parentElement.clientWidth - gap * (state.widths.length - 1) : 0;
    const mins = (state.root.dataset.resizeGridMins || '').split(',').map(Number);
    state.widths = state.widths.map((width, index) => Math.max(width, mins[index] || 0));
    state.applied = state.balance ? fitGridWidths(state.widths, mins, room) : state.widths;
    const template = state.applied.map((width) => `${width}px`).join(' ');
    const minWidth = state.applied.reduce((sum, width) => sum + width, 0) + gap * (state.applied.length - 1);
    state.root.querySelectorAll('[data-resize-grid-header], [data-resize-grid-row]').forEach((row) => {
      if (row.dataset.resizeGridHeader !== state.group && row.dataset.resizeGridRow !== state.group) return;
      row.style.gridTemplateColumns = template;
      row.style.minWidth = `${minWidth}px`;
    });
    syncHeaderScroll(state.root);
  };
  const applyFlex = (state) => {
    let total = 0;
    state.columns.forEach((column) => {
      const width = clampColumnWidth(state.widths[column.dataset.resizeFlexCol], Number(state.root.dataset.resizeFlexMin) || 190, 700);
      state.widths[column.dataset.resizeFlexCol] = width;
      column.style.flex = `0 0 ${width}px`;
      total += width;
    });
    const style = win.getComputedStyle(state.root);
    const gap = parseFloat(style.columnGap) || 0;
    const padding = (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.paddingRight) || 0);
    state.root.style.minWidth = `${total + gap * (state.columns.length - 1) + padding}px`;
  };
  const schedule = () => {
    if (queued) return;
    queued = true;
    win.requestAnimationFrame(scan);
  };

  const edgeCell = (event) => {
    const cell = event.target?.closest?.('th.pb-auto-resize-head');
    if (!cell) return null;
    const rect = cell.getBoundingClientRect();
    if (event.clientX < rect.right - 9 || event.clientX > rect.right + 4) return null;
    const table = cell.closest('table');
    const state = states.get(table);
    return state ? { state, index: Number(cell.dataset.autoColIndex) } : null;
  };
  const edgeGridCell = (event) => {
    const cell = event.target?.closest?.('[data-auto-grid-col-index]');
    const header = cell?.closest?.('[data-resize-grid-header]');
    const state = header && gridStates.get(header);
    if (!state) return null;
    const rect = cell.getBoundingClientRect();
    if (event.clientX < rect.right - 9 || event.clientX > rect.right + 4) return null;
    return { state, index: Number(cell.dataset.autoGridColIndex), grid: true };
  };
  const edgeFlexCell = (event) => {
    const head = event.target?.closest?.('[data-resize-flex-head]');
    const column = head?.closest?.('[data-resize-flex-col]');
    const root = column?.closest?.('[data-resize-flex-root]');
    const state = root && flexStates.get(root);
    if (!state) return null;
    const rect = head.getBoundingClientRect();
    if (event.clientX < rect.right - 9 || event.clientX > rect.right + 4) return null;
    return { state, index: column.dataset.resizeFlexCol, flex: true };
  };
  const save = (state) => {
    try { win.localStorage.setItem(state.key, JSON.stringify(state.widths)); } catch { /* storage unavailable */ }
  };
  // Slepen, dubbelklikken en Alt+pijl rekenen vanaf wat er op het scherm staat,
  // niet vanaf de voorkeur die applyGrid passend maakte.
  const adoptApplied = (state) => { if ((state.balance || state.fit) && state.applied) state.widths = state.applied.slice(); };
  const resizeWidth = (state, index, wanted, min, max) => {
    if (state.fit) { state.widths = fitNeighbourResize(state.widths, state.mins, index, wanted); return; }
    if (!state.balance) { state.widths[index] = clampColumnWidth(wanted, min, max); return; }
    const neighbor = index < state.widths.length - 1 ? index + 1 : index - 1;
    const mins = (state.root.dataset.resizeGridMins || '').split(',').map(Number);
    const ownMin = Number.isFinite(mins[index]) && mins[index] > 0 ? mins[index] : min;
    const neighborMin = Number.isFinite(mins[neighbor]) && mins[neighbor] > 0 ? mins[neighbor] : min;
    const pair = state.widths[index] + state.widths[neighbor];
    const next = Math.max(ownMin, Math.min(max, pair - neighborMin, Math.round(wanted)));
    state.widths[index] = next;
    state.widths[neighbor] = pair - next;
  };
  const onDown = (event) => {
    if (event.button !== 0) return;
    const hit = edgeCell(event) || edgeGridCell(event) || edgeFlexCell(event);
    if (!hit) return;
    event.preventDefault();
    event.stopPropagation();
    if (hit.grid || hit.state.fit) adoptApplied(hit.state);
    active = { ...hit, x: event.clientX, width: hit.state.widths[hit.index] };
    doc.documentElement.style.cursor = 'col-resize';
    doc.documentElement.style.userSelect = 'none';
  };
  const onMove = (event) => {
    if (!active) return;
    resizeWidth(active.state, active.index, active.width + event.clientX - active.x, active.flex ? 190 : AUTO_COL_MIN, active.flex ? 700 : AUTO_COL_MAX);
    if (active.flex) applyFlex(active.state);
    else if (active.grid) applyGrid(active.state);
    else apply(active.state);
  };
  const onUp = () => {
    if (!active) return;
    save(active.state);
    active = null;
    swallowClickUntil = Date.now() + 250;
    doc.documentElement.style.cursor = '';
    doc.documentElement.style.userSelect = '';
  };
  const onClick = (event) => {
    if (Date.now() < swallowClickUntil && (edgeCell(event) || edgeGridCell(event) || edgeFlexCell(event))) {
      event.preventDefault();
      event.stopPropagation();
    }
  };
  const onDoubleClick = (event) => {
    const hit = edgeCell(event) || edgeGridCell(event) || edgeFlexCell(event);
    if (!hit) return;
    event.preventDefault();
    event.stopPropagation();
    if (hit.flex) {
      hit.state.widths[hit.index] = clampColumnWidth(hit.state.columns.find((column) => column.dataset.resizeFlexCol === hit.index)?.querySelector('[data-resize-flex-head]')?.scrollWidth + 12, 190, 700);
      applyFlex(hit.state);
    } else if (hit.grid) {
      adoptApplied(hit.state);
      const cell = hit.state.cells[hit.state.indexes.indexOf(hit.index)];
      resizeWidth(hit.state, hit.index, (cell?.scrollWidth || 0) + 12, AUTO_COL_MIN, AUTO_COL_MAX);
      applyGrid(hit.state);
    } else {
      const rows = [hit.state.table.tHead.rows[0], ...Array.from(hit.state.table.tBodies[0]?.rows || []).slice(0, 100)];
      const widest = Math.max(...rows.map((row) => row.cells[hit.index]?.scrollWidth || 0));
      adoptApplied(hit.state);
      resizeWidth(hit.state, hit.index, clampColumnWidth(widest + 12), AUTO_COL_MIN, AUTO_COL_MAX);
      apply(hit.state);
    }
    save(hit.state);
  };
  const onKeyDown = (event) => {
    if (!event.altKey || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
    const cell = event.target?.closest?.('th.pb-auto-resize-head, [data-auto-grid-col-index], [data-resize-flex-head]');
    const state = cell && (states.get(cell.closest('table')) || gridStates.get(cell.closest('[data-resize-grid-header]')) || flexStates.get(cell.closest('[data-resize-flex-root]')));
    if (!state) return;
    event.preventDefault();
    event.stopPropagation();
    if (state.header || state.fit) adoptApplied(state);
    const index = state.root && !state.header ? cell.closest('[data-resize-flex-col]').dataset.resizeFlexCol : Number(cell.dataset.autoColIndex ?? cell.dataset.autoGridColIndex);
    resizeWidth(state, index, state.widths[index] + (event.key === 'ArrowRight' ? 16 : -16), state.root && !state.header ? 190 : AUTO_COL_MIN, state.root && !state.header ? 700 : AUTO_COL_MAX);
    if (state.root && !state.header) applyFlex(state);
    else if (state.header) applyGrid(state);
    else apply(state);
    save(state);
  };

  const observer = new win.MutationObserver((records) => {
    if (records.some((record) => record.type === 'childList')) schedule();
  });
  observer.observe(doc.body, { childList: true, subtree: true });
  const onScroll = (event) => {
    const root = event.target?.closest?.('[data-resize-grid-root]');
    if (root) syncHeaderScroll(root);
  };
  doc.addEventListener('scroll', onScroll, true);
  win.addEventListener('resize', schedule);
  doc.addEventListener('pointerdown', onDown, true);
  doc.addEventListener('pointermove', onMove, true);
  doc.addEventListener('pointerup', onUp, true);
  doc.addEventListener('pointercancel', onUp, true);
  doc.addEventListener('click', onClick, true);
  doc.addEventListener('dblclick', onDoubleClick, true);
  doc.addEventListener('keydown', onKeyDown, true);
  schedule();
  return () => {
    observer.disconnect();
    fitObserver?.disconnect();
    doc.removeEventListener('scroll', onScroll, true);
    win.removeEventListener('resize', schedule);
    doc.removeEventListener('pointerdown', onDown, true);
    doc.removeEventListener('pointermove', onMove, true);
    doc.removeEventListener('pointerup', onUp, true);
    doc.removeEventListener('pointercancel', onUp, true);
    doc.removeEventListener('click', onClick, true);
    doc.removeEventListener('dblclick', onDoubleClick, true);
    doc.removeEventListener('keydown', onKeyDown, true);
    onUp();
  };
}
